import { NgTemplateOutlet } from '@angular/common';
import {
  AfterContentInit,
  Component,
  ContentChild,
  ContentChildren,
  DestroyRef,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Sort } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatNoDataRow,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ListPagination } from '@workspace/data-types';
import { ApplicationStore } from '@workspace/ng-data-access';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { AuraClipboardComponent } from '../buttons';
import { ColumnRecord } from './columns-customization/columns-customization.component';
import { DataHeaderAppliedFilter, DataHeaderFilter } from './data-filters/data-header-filter.interface';
import { AuraDataHeaderActions } from './data-header-actions/data-header-actions.component';
import { ActionButton, ActionMenu, DataListColumn, DataWithCheckbox } from './data-table.interface';

export type TableColumnKey = 'debug' | 'checkbox' | 'actions';

@Component({
  selector: 'aura-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.css'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatIcon,
    FormsModule,
    ReactiveFormsModule,
    MatMenuTrigger,
    MatMenuItem,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    AuraClipboardComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatNoDataRow,
    NgTemplateOutlet,
    MatPaginator,
    MatCheckboxModule,
    MatMenu,
    MatMenuContent,
    AuraDataHeaderActions,
    MatProgressBarModule,
    MatTooltipModule,
    NgxSkeletonLoaderComponent,
  ],
  host: {
    '(window:keydown)': 'handleKeyboard($event)',
  },
})
export class DataTableComponent<T, K extends string = TableColumnKey> implements AfterContentInit {
  @ContentChildren(MatHeaderRowDef, { descendants: true }) headerRowDefs!: QueryList<MatHeaderRowDef>;
  @ContentChildren(MatRowDef, { descendants: true }) rowDefs!: QueryList<MatRowDef<T>>;
  @ContentChildren(MatColumnDef, { descendants: true }) columnDefs!: QueryList<MatColumnDef>;
  @ContentChild(MatNoDataRow) noDataRow!: MatNoDataRow;
  @ContentChild('filterTemplate') filterTemplate!: TemplateRef<Record<string, unknown>>;

  @ViewChild(MatTable, { static: true }) table!: MatTable<T>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /*
   **  Data Header input Actions
   */
  hideSearch = input<boolean>(false);
  topMenu = input<ActionMenu | null>(null);
  topButtons = input<ActionButton[]>([]);
  searchPlaceHolder = input<string>('Rechercher');
  searchChanged = output<string>();
  filterViewChanged = output<string>();
  filters = input<DataHeaderFilter<K>[]>([]);
  triggerFiltering = output<null>();
  triggerRefresh = output<null>();
  clearFiltering = output<null>();
  mappedFilters = input<DataHeaderAppliedFilter<K>[]>();
  removeFilter = output<{ value: string; key: string }>();

  inputColumns = input.required<DataListColumn<K>[]>();
  columns = signal<DataListColumn<K | TableColumnKey>[]>([]);
  readonly columnKeys = computed(() =>
    this.columns()
      .filter((col) => col.display !== false)
      .map((col) => col.key),
  );

  loading = input<boolean>(false);
  skeletonRowsCount = input<number>(8);
  readonly skeletonRows = computed(() => {
    const size = this.skeletonRowsCount() || this.pagination()?.pageSize || 8;
    return Array.from({ length: Math.min(Math.max(size, 5), 10) }, (_, i) => i);
  });
  displayCheckbox = input<boolean>(false);
  items = input.required<T[]>();
  readonly localItems = signal<DataWithCheckbox<T>[]>([]);
  readonly selectedCount = signal(0);
  count = input.required<number>();
  pagination = input.required<ListPagination>();
  disableItemClick = input<boolean>(false);
  rowClickable = input<boolean>(false);
  hidePagination = input<boolean>(false);
  itemActions = input<ActionButton[]>([]);

  noDataLabel = input<string>('Aucune donnée à afficher');
  expandable = input<boolean>(false);

  itemClicked = output<T>();
  itemAuxClicked = output<T>();
  pageChanged = output<PageEvent>();
  rowsSelected = output<DataWithCheckbox<T>[]>();
  menuOpened = output<T>();
  sortChanged = output<Sort>();

  debouncedPagination$ = new Subject<PageEvent>();
  debouncedSort$ = new Subject<Sort>();

  allRowsSelected = signal<boolean>(false);
  destroyRef = inject(DestroyRef);
  appStore = inject(ApplicationStore);

  constructor() {
    effect(() => {
      this.localItems.set(
        this.items().map((item) => ({
          ...item,
          selected: false, // default
        })),
      );
    });
    effect(() => {
      // Initialize columns
      const columns: DataListColumn<K | TableColumnKey>[] = [...this.inputColumns()];

      if (this.appStore.debugMode()) {
        columns.unshift({
          key: 'debug' as TableColumnKey,
          label: 'Debug',
          display: true,
          native: true,
        });
      }

      if (this.displayCheckbox()) {
        columns.unshift({
          key: 'checkbox' as TableColumnKey,
          label: 'Select',
          display: true,
          native: true,
        });
      }

      if (this.itemActions()?.length > 0) {
        columns.push({
          key: 'actions' as TableColumnKey,
          label: 'Actions',
          display: true,
          native: true,
        });
      }
      this.columns.set(columns);
    });
    this.debouncedPagination$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((event: PageEvent) => {
        this.pageChanged.emit(event);
      });

    this.debouncedSort$
      .pipe(debounceTime(3000), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((newSort: Sort) => {
        this.sortChanged.emit(newSort);
      });
  }

  ngAfterContentInit() {
    this.columnDefs.forEach((columnDef) => this.table.addColumnDef(columnDef));
    this.rowDefs.forEach((rowDef) => this.table.addRowDef(rowDef));
    this.headerRowDefs.forEach((headerRowDef) => this.table.addHeaderRowDef(headerRowDef));
    this.table.setNoDataRow(this.noDataRow);

    this.columnDefs.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.columnDefs.forEach((columnDef) => this.table.addColumnDef(columnDef));
    });
    this.rowDefs.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.rowDefs.forEach((rowDef) => this.table.addRowDef(rowDef));
    });
    this.headerRowDefs.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.headerRowDefs.forEach((headerRowDef) => this.table.addHeaderRowDef(headerRowDef));
    });
  }

  handleKeyboard(event: KeyboardEvent) {
    if (!event.shiftKey && event.altKey && event.code === 'ArrowRight') {
      event.preventDefault();
      if (this.paginator.hasNextPage()) {
        this.paginator.nextPage();
      }
    } else if (!event.shiftKey && event.altKey && event.code === 'ArrowLeft') {
      event.preventDefault();
      if (this.paginator.hasPreviousPage()) {
        this.paginator.previousPage();
      }
    } else if (event.shiftKey && event.altKey && event.code === 'ArrowRight') {
      event.preventDefault();
      const lastPageIndex = Math.ceil(this.paginator.length / this.paginator.pageSize) - 1;
      this.paginator.pageIndex = lastPageIndex;
      this.paginator._changePageSize(this.paginator.pageSize);
    } else if (event.shiftKey && event.altKey && event.code === 'ArrowLeft') {
      event.preventDefault();
      this.paginator.firstPage();
    }
  }

  selectAllRows(): void {
    const updatedItems = this.localItems().map((item) => ({
      ...item,
      selected: this.allRowsSelected(),
    }));
    this.localItems.set(updatedItems);
    this.selectedCount.set(updatedItems.filter((item) => item.selected).length);
    this.rowsSelected.emit(updatedItems);
  }

  onRowSelect(updatedItem: DataWithCheckbox<T>): void {
    const updatedList = this.localItems().map((item) =>
      item._id === updatedItem._id ? { ...item, selected: updatedItem.selected } : item,
    );
    this.localItems.set(updatedList);
    const count = updatedList.reduce((acc, item) => acc + (item.selected ? 1 : 0), 0);
    if (count === this.localItems().length) {
      this.allRowsSelected.set(true);
    } else {
      this.allRowsSelected.set(false);
    }
    this.selectedCount.set(count);
    this.rowsSelected.emit(updatedList.filter((item) => item.selected));
  }

  columnChanged(dto: ColumnRecord): void {
    const updated = this.columns().map((col) => ({
      ...col,
      display: dto[col.key] ?? col.display,
    }));
    this.columns.set(updated);
  }

  clearSelection(): void {
    const updatedItems = this.localItems().map((item) => ({
      ...item,
      selected: false,
    }));
    this.localItems.set(updatedItems);
    this.selectedCount.set(updatedItems.filter((item) => item.selected).length);
    this.allRowsSelected.set(false);
    this.rowsSelected.emit(updatedItems);
  }

  onItemClicked(item: T): void {
    if (!this.rowClickable() || this.disableItemClick()) {
      return;
    }
    const selection = window.getSelection();
    if (selection && selection.toString().trim().length > 0) {
      return;
    }
    this.itemClicked.emit(item);
  }

  onItemAuxClicked(item: T): void {
    // middle mouse button
    if (!this.rowClickable() || this.disableItemClick()) {
      return;
    }
    this.itemAuxClicked.emit(item);
  }

  onPageChanged(event: PageEvent) {
    this.debouncedPagination$.next(event);
  }

  onMenuOpened(item: T) {
    this.menuOpened.emit(item);
  }

  onSortChanged(newSort: Sort) {
    this.debouncedSort$.next(newSort);
  }

  goToFirstPaginationPage() {
    this.paginator.firstPage();
  }

  onSearchChanged(search: string) {
    this.pagination().search = search;
    this.searchChanged.emit(search);
    this.goToFirstPaginationPage();
  }

  onFilterViewChanged(view: string) {
    this.filterViewChanged.emit(view);
  }

  getSkeletonWidth(rowIndex: number, colIndex: number): string {
    const widths = ['75%', '55%', '85%', '65%', '45%', '90%'];
    const idx = (rowIndex * 3 + colIndex) % widths.length;
    return widths[idx];
  }
}
